import IProduct from 'models/product';
import { IFoodlaCategoryOption } from 'models/category';
import { APPLICATION_ROLES, SUPERUSER_APPROVAL } from 'components/constants-ts';
import { getRequiredProductFields } from '../CheckRequireError';
import { isNewFood } from '../fields/category';
import { isEnergyError } from '../fields/nutrition';
import { checkShortSalesDescriptionError } from '../fields/short-sales-description';
import { evaluateProductValidations } from './evaluateProductValidations';
import { ProductFieldRuleType, ProductRuleFieldType, ProductValidations } from './type';
import { getMissingNutritionalFields, getRequiredNutritionalFields } from './validators';
import { getFormattedValue, getTemperatureError } from 'components/inputfields/TemperatureField';
import { isBlank } from 'utils/helpers-ts';
import { PHRASE_FILL_CODE_FIELDS } from 'constants/bulk-import';
import { getPhraseSpec } from '../fields/hazard-placeholders';

export type ProductSaveError = {
  field?: ProductRuleFieldType;
  message: string;
  rule?: ProductFieldRuleType;
  required?: boolean;
};

export type ProductSaveValidationParams = {
  state?: IProduct;
  savedProduct?: Partial<IProduct>;
  isCreatingNew: boolean;
  rootCategory?: IFoodlaCategoryOption;
  userRole?: string;
  imageCount: number;
  productInfoFilesCount?: number;
  readOnly?: boolean;
  processing?: boolean;
  saving?: boolean;
  disabled?: boolean;
  eanError?: string;
  nonUniqueEanError?: string;
  categoryError?: boolean;
  allReviewFieldsApproved?: boolean;
  isFood?: boolean;
  /** The native form already evaluates these for its field context. */
  required?: ProductValidations;
};

export type ProductSaveValidation = {
  required: ProductValidations;
  /** Native submission requirements, including fields that are already filled. */
  requiredSubmissionFields: ProductRuleFieldType[];
  submissionErrors: ProductSaveError[];
  draftErrors: ProductSaveError[];
  canSubmit: boolean;
  canSaveDraft: boolean;
};

/** Save-action policy shared by the product form and bulk import, on native-format values. */
export const getProductSaveValidation = ({
  state,
  savedProduct,
  isCreatingNew,
  rootCategory,
  userRole,
  imageCount,
  productInfoFilesCount,
  readOnly,
  processing,
  saving,
  disabled,
  eanError,
  nonUniqueEanError,
  categoryError,
  allReviewFieldsApproved = true,
  isFood,
  required: precomputedRequired,
}: ProductSaveValidationParams): ProductSaveValidation => {
  const required = precomputedRequired ?? evaluateProductValidations({
    state,
    rootCategory,
    productInfoFilesCount,
    applyRetailerRules: isCreatingNew || !!savedProduct?.isDraft,
  });
  const isSuperuser = userRole === APPLICATION_ROLES.SUPERUSER;
  const isProducer = userRole === APPLICATION_ROLES.PRODUCER || userRole === APPLICATION_ROLES.STORE;
  const isLogistician = userRole === APPLICATION_ROLES.LOGISTICIAN;
  const isApproved = state?.adminStatus === SUPERUSER_APPROVAL.ADMIN_APPROVED;
  const isAdministrativeSave = isSuperuser && state?.adminStatus === SUPERUSER_APPROVAL.ADMIN_DISAPPROVED;
  const operationErrors: ProductSaveError[] = [];
  if (readOnly) operationErrors.push({ message: 'Produkten är skrivskyddad.' });
  if (processing) operationErrors.push({ field: 'image_src', message: 'Vänta tills bilderna är klara.' });
  if (saving) operationErrors.push({ message: 'Produkten sparas. Vänta tills den är klar.' });

  const draftErrors = [...operationErrors];
  if (savedProduct?.wasApproved || savedProduct?.adminStatus === SUPERUSER_APPROVAL.ADMIN_APPROVED || isApproved) {
    draftErrors.push({ message: 'En godkänd eller tidigare godkänd produkt kan inte sparas som utkast.' });
  }

  const submissionErrors = [...operationErrors];
  const requiredSubmissionFields = new Set<ProductRuleFieldType>();
  // The native administrative failed-review save bypasses activation checks, but never an in-flight save.
  if (!isAdministrativeSave) {
    if (disabled) submissionErrors.push({ message: 'Produkten kan inte sparas just nu.' });
    requiredSubmissionFields.add('image_src');
    if (imageCount <= 0) {
      submissionErrors.push({ field: 'image_src', message: 'Produktbild för e-handel är obligatoriskt', required: true });
    }
    if (state?.adminStatus !== SUPERUSER_APPROVAL.ADMIN_DISAPPROVED) {
      const baseFields = getRequiredProductFields(state, rootCategory);
      baseFields.forEach(({ field }) => requiredSubmissionFields.add(field));
      submissionErrors.push(...baseFields.filter(({ missing }) => missing).map(({ field, label }) => ({
        field, message: `${label} är obligatoriskt`, required: true,
      })));
    }
    (Object.keys(required.requiredFields) as ProductRuleFieldType[]).forEach(field => {
      const codeField = PHRASE_FILL_CODE_FIELDS[field as keyof typeof PHRASE_FILL_CODE_FIELDS];
      if (codeField) {
        const codes = state?.[codeField] as string[] | undefined;
        if (!codes?.some(code => getPhraseSpec(code)?.slots.some(slot => !slot.optional))) return;
      }
      requiredSubmissionFields.add(field);
    });
    submissionErrors.push(...required.errors.map(({ field, message, rule }) => ({ field, message, rule })));

    const shortSalesError = checkShortSalesDescriptionError(state?.shortSalesDescription);
    if (shortSalesError) submissionErrors.push({ field: 'shortSalesDescription', message: shortSalesError });
    if ((isFood ?? isNewFood(state?.foodlaCategory, rootCategory)) && isEnergyError(state)) {
      submissionErrors.push({ field: 'energi_kj', message: 'Värdena för kJ och kcal stämmer inte överens.' });
    }
    if (isSuperuser && isApproved) {
      requiredSubmissionFields.add('foodlaCategory');
      if (categoryError) submissionErrors.push({ field: 'foodlaCategory', message: 'Välj en kategori på lägsta nivån.' });
    }
    if ((isApproved || isProducer || isLogistician) && eanError) {
      submissionErrors.push({ field: 'EAN', message: eanError });
    }
    if (nonUniqueEanError) submissionErrors.push({ field: 'EAN', message: nonUniqueEanError });
    if (isFood ?? isNewFood(state?.foodlaCategory, rootCategory)) {
      getRequiredNutritionalFields(state).forEach(field => requiredSubmissionFields.add(field));
      getMissingNutritionalFields(state).forEach(field => submissionErrors.push({
        field, message: 'Fyll i obligatoriska näringsvärden.', required: true,
      }));
    }
    if (!state?.producerIsStore) {
      for (const [minField, maxField] of [
        ['min_temperature', 'max_temperature'],
        ['transportMinTemperature', 'transportMaxTemperature'],
      ] as const) {
        if (getFormattedValue(state?.[minField]) !== '' || getFormattedValue(state?.[maxField]) !== '') {
          requiredSubmissionFields.add(minField);
          requiredSubmissionFields.add(maxField);
        }
        const error = getTemperatureError(state?.[minField], state?.[maxField]);
        if (error?.min) submissionErrors.push({
          field: minField, message: 'Fyll i temperaturuppgifterna.', required: isBlank(state?.[minField]),
        });
        if (error?.max) submissionErrors.push({
          field: maxField, message: 'Fyll i temperaturuppgifterna.', required: isBlank(state?.[maxField]),
        });
      }
    }
    if (isSuperuser && isApproved && !allReviewFieldsApproved) {
      submissionErrors.push({ message: 'Alla granskningsfält måste vara godkända innan produkten kan godkännas.' });
    }
  }

  return {
    required,
    requiredSubmissionFields: Array.from(requiredSubmissionFields),
    submissionErrors,
    draftErrors,
    canSubmit: submissionErrors.length === 0,
    canSaveDraft: draftErrors.length === 0,
  };
};
